import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelection } from '@/contexts/SelectionContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowUpRight, ArrowDownRight, ArrowLeftRight, Minus, Rss, Info, Search, SearchX, Hourglass, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';
import { useCtrImpactV2, useLatestPerformanceDate, useLiftCurve, useDisapprovedProducts, buildDisapprovedMap, formatMetricNumber, CTR_IMPACT_PAGE_LIMIT } from '@/hooks/usePerformance';
import type { CtrImpactRowV2 } from '@/hooks/usePerformance';
import { useStability, buildStabilityMap } from '@/hooks/useStability';
import { SignificanceBadge } from '@/components/performance/SignificanceBadge';
import { StabilityChip } from '@/components/performance/StabilityChip';
import { PerformanceSigKpiCards } from '@/components/performance/PerformanceSigKpiCards';
import { PerformanceLiftCurve } from '@/components/performance/PerformanceLiftCurve';
import { ExperimentCard } from '@/components/performance/ExperimentCard';
import { PerformanceEmptyState } from '@/components/performance/PerformanceEmptyState';
import { DisapprovedBadge } from '@/components/performance/DisapprovedBadge';
import { formatCtrCI, medianOf, passesDataGates, clickWeightedLift, type StatsLocale } from '@/lib/statistics';
import { PageHeader } from '@/components/PageHeader';
import { formatDecimal, formatSignedPercent, numberLocale } from '@/lib/language';

const PerformanceDashboard: React.FC = () => {
  const { t, i18n } = useTranslation('performance');
  const { selectedFeedId, feed } = useSelection();
  const { user } = useAuth();
  // F18: eine Ableitung fuer Datum UND Zahl. Zwei Schreibweisen im selben Bild
  // ("729.5 %" neben "p < 0,05", "1000" neben "3.769") sind auf einer Seite,
  // deren einziges Versprechen Messgenauigkeit ist, kein Detail.
  const dateLocale = numberLocale(i18n.language);
  const language = i18n.language;
  // Spannen werden ausgeschrieben ("bis" / "to"), siehe RANGE_SEPARATORS in
  // lib/statistics.ts. Gleiche Ableitung wie in SignificanceBadge.
  const statsLocale: StatsLocale = i18n.language?.startsWith('de') ? 'de' : 'en';
  const [showAllImpact, setShowAllImpact] = useState(false);
  const [windowDays, setWindowDays] = useState(14);
  const [searchQuery, setSearchQuery] = useState('');
  const [impactFilter, setImpactFilter] = useState<'all' | 'improved' | 'declined'>('all');
  const [onlySignificant, setOnlySignificant] = useState(true);
  const [sortBy, setSortBy] = useState<'change_desc' | 'change_asc' | 'date_desc'>('change_desc');

  const { data: latestDate, isLoading: latestDateLoading } = useLatestPerformanceDate(selectedFeedId);
  const { data: ctrImpactData, isLoading: ctrLoading, isError: isCtrError, refetch: refetchCtrImpact } = useCtrImpactV2(selectedFeedId, { windowDays });
  const { data: stabilityRows = [], isLoading: stabilityLoading } = useStability(selectedFeedId);
  const { data: liftCurvePoints = [], isLoading: liftCurveLoading } = useLiftCurve(selectedFeedId, windowDays);
  const { data: disapprovedRows = [] } = useDisapprovedProducts(selectedFeedId);
  const stabilityMap = buildStabilityMap(stabilityRows);
  // Googles Auslieferungs-Urteil je Produkt (`products.shopping_ads_disapproved_at`,
  // Serving-Gate seit Migration `20260824090000`). Steht ein Produkt hier drin,
  // liefert Google es aktuell nicht aus — seine Nachher-Zahlen sind dann nicht
  // das Ergebnis der Optimierung, sondern das Ergebnis der Ablehnung.
  const disapprovedMap = buildDisapprovedMap(disapprovedRows);

  const hasError = isCtrError;

  const impactProducts = ctrImpactData || [];

  // `get_optimization_impact_v2` liefert hoechstens `CTR_IMPACT_PAGE_LIMIT`
  // Zeilen. Kommen genau so viele zurueck, ist die Liste abgeschnitten und JEDE
  // daraus abgeleitete Anzahl nur eine untere Schranke — die Lift-Kurve schreibt
  // ihr n dann als "mindestens" aus, statt eine Zahl zu behaupten, die sie nicht
  // kennt. Die Kurve selbst rechnet serverseitig ueber alle Produkte und ist von
  // dem Deckel nicht betroffen.
  const impactListTruncated = impactProducts.length >= CTR_IMPACT_PAGE_LIMIT;

  // Aggregate KPIs are based on the strict "significant" subset (passes all gates AND |z|>=1.96).
  // "Trustworthy" = passes all gates regardless of significance — used as the denominator.
  const significantProducts = impactProducts.filter(p => p.is_significant);
  const trustworthyProducts = impactProducts.filter(p => passesDataGates(p));
  const significantDeltas = significantProducts
    .map(p => p.ctr_change_pct)
    .filter((d): d is number => d !== null);
  const meanChange = significantDeltas.length > 0
    ? significantDeltas.reduce((sum, d) => sum + d, 0) / significantDeltas.length
    : null;
  const medianChange = medianOf(significantDeltas);
  const clickWeightedChange = clickWeightedLift(significantProducts.map(p => ({
    pre_clicks: p.pre_clicks,
    pre_impressions: p.pre_impressions,
    post_clicks: p.post_clicks,
    post_impressions: p.post_impressions,
  })));
  // ZAEHLT PRODUKTE, NICHT ZEILEN: die RPC liefert je Optimierungslauf eine
  // Zeile (`optimization_number`), ein zweimal optimiertes abgelehntes Produkt
  // stuende sonst zweimal in der Zahl. Und sie zaehlt die Schnittmenge mit der
  // Impact-Liste statt feedweit: die Kopfzeile spricht ueber diesen Vergleich,
  // nicht ueber den Feed.
  const disapprovedInImpact = new Set(
    impactProducts.filter((p) => disapprovedMap.has(p.product_id)).map((p) => p.product_id),
  ).size;
  const significantCount = significantProducts.length;
  const trustworthyCount = trustworthyProducts.length;
  const improved = significantProducts.filter(p => (p.ctr_change_pct ?? 0) > 0).length;
  const declined = significantProducts.filter(p => (p.ctr_change_pct ?? 0) < 0).length;

  const filteredProducts = impactProducts.filter(p => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!p.product_title?.toLowerCase().includes(q)) return false;
    }
    if (onlySignificant && !p.is_significant) return false;
    if (impactFilter === 'improved') return p.ctr_change_pct !== null && p.ctr_change_pct > 0;
    if (impactFilter === 'declined') return p.ctr_change_pct !== null && p.ctr_change_pct < 0;
    return true;
  });

  // Sort applied after filtering. Nulls always sink to the bottom regardless of direction —
  // a product with no comparable delta shouldn't outrank one that has one.
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'date_desc') {
      return new Date(b.optimized_at).getTime() - new Date(a.optimized_at).getTime();
    }
    const aVal = a.ctr_change_pct;
    const bVal = b.ctr_change_pct;
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;
    return sortBy === 'change_desc' ? Number(bVal) - Number(aVal) : Number(aVal) - Number(bVal);
  });

  // Task 12: shared between the desktop table row and the mobile card so the
  // two views can never drift on what "hasMultiple" / "stability" mean.
  const getRowMeta = (row: CtrImpactRowV2) => {
    const change = row.ctr_change_pct;
    const hasMultiple = impactProducts.filter((p: CtrImpactRowV2) => p.product_id === row.product_id).length > 1;
    const stability = stabilityMap.get(`${row.product_id}:${row.optimization_number}`);
    // `undefined` heisst "nicht abgelehnt", ein leeres Array "abgelehnt, Grund
    // nicht mitgeliefert". Die Unterscheidung traegt die Existenz des
    // Schluessels, nicht die Laenge der Liste.
    const disapprovedIssues = disapprovedMap.get(row.product_id);
    return { change, hasMultiple, stability, disapprovedIssues };
  };

  return (
    <div className="space-y-6">
      {/* Task 11: die Feed-Auswahl kommt aus der Kopfzeile (Layout.tsx). Damit
          faellt hier auch `filterByPerformance` weg — der globale Selektor
          zeigt alle Feeds. Wer einen ohne Messdaten waehlt, sieht den
          vorhandenen Leerzustand `PerformanceEmptyState` weiter unten. */}
      <PageHeader
        title={t('title')}
        subtitle={selectedFeedId ? t('subtitle', { feed: feed?.display_name ?? '' }) : undefined}
        actions={selectedFeedId ? (
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('comparison.windowDays', { days: 7 })}</SelectItem>
              <SelectItem value="14">{t('comparison.windowDays', { days: 14 })}</SelectItem>
              <SelectItem value="30">{t('comparison.windowDays', { days: 30 })}</SelectItem>
            </SelectContent>
          </Select>
        ) : undefined}
      />

      {!selectedFeedId ? (
        <EmptyState
          icon={Rss}
          title={t('empty.noFeed')}
          description={t('empty.noFeedDesc')}
        />
      ) : hasError ? (
        // Exklusiver Zweig (Muster FeedHealthDashboard): ein Ladefehler ist
        // KEINE Messung. Vorher standen unter dem Fehlerbanner drei Sektionen,
        // die gemessene Leere behaupteten (0-KPIs, "Daten werden noch
        // gesammelt", "keine Vergleichsdaten") — zwei sich widersprechende
        // Zustaende im selben Viewport, und wer den Banner ueberscrollt, nimmt
        // die falsche Aussage mit.
        <ErrorState description={t('errors.loadFailed')} onRetry={() => refetchCtrImpact()} />
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
            <Info className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              {/* Ein Datenstand ist eine Messung, kein Versprechen: ohne
                  product_performance-Zeile gibt es KEIN Datum — das fruehere
                  "heute minus 2 Tage" war eine aus der Client-Uhr erfundene
                  Zahl. Waehrend die Abfrage laeuft, bleibt die Angabe weg. */}
              {t('latency.notice')}
              {!latestDateLoading && (
                <>
                  {' '}
                  {latestDate
                    ? t('latency.lastUpdated', {
                        date: new Date(latestDate + 'T00:00:00').toLocaleDateString(dateLocale),
                      })
                    : t('latency.noData')}
                </>
              )}
            </span>
          </div>

          <ExperimentCard feedId={selectedFeedId} />

          {ctrLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <PerformanceSigKpiCards
              significantCount={significantCount}
              // Die Richtung gehoert an die Zahl: `significantCount` stammt aus
              // einem ZWEISEITIGEN Gate (|z| >= 1.96) und enthaelt Rueckgaenge.
              improvedCount={improved}
              declinedCount={declined}
              medianLiftPct={medianChange}
              clickWeightedLiftPct={clickWeightedChange}
              // F16/F19: Bezugsgroesse der Kacheln. Ohne sie steht dort eine
              // nackte Null bzw. ein stummer Strich, und beides laesst offen,
              // ob nichts gemessen oder nichts gefunden wurde.
              trustworthyCount={trustworthyCount}
            />
          )}

          {ctrLoading ? (
            <Skeleton className="h-64 w-full rounded-lg" />
          ) : significantCount === 0 ? (
            <PerformanceEmptyState
              trustworthyCount={trustworthyCount}
              onShowAllTrustworthy={() => setOnlySignificant(false)}
            />
          ) : liftCurveLoading ? (
            <Skeleton className="h-64 w-full rounded-lg" />
          ) : (
            <PerformanceLiftCurve
              points={liftCurvePoints}
              windowDays={windowDays}
              uploadDatesByProduct={significantProducts.map(p => new Date(p.uploaded_at))}
              sampleSizeCapped={impactListTruncated}
            />
          )}

          {/* CTR Impact Table */}
          <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              {/* `ArrowLeftRight` statt des frueheren `TrendingUp`: derselbe
                  Befund wie am Stabilitaets-Chip und an den KPI-Kacheln, nur
                  eine Ebene hoeher. Dieser Titel steht ueber der GESAMTEN
                  zweiseitigen Vergleichstabelle — inklusive der
                  Aenderungs-Spalte, die pro Zeile bis -100 % gehen kann, und
                  inklusive der Mittelwert- und Median-Angabe rechts im Kopf,
                  die beide `formatSignedPercent` rendern. Ein Aufwaertspfeil
                  darueber behauptet ein Ergebnis, das die Tabelle erst zeigen
                  soll. Der Doppelpfeil ist waagerecht und passt zur Sache
                  ("Vorher / Nachher"), ohne eine Richtung zu nennen. */}
              <ArrowLeftRight className="h-4 w-4 text-primary" />
              {t('comparison.title')}
              <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
                <SelectTrigger className="h-7 text-xs w-[80px] ml-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">{t('comparison.windowDays', { days: 7 })}</SelectItem>
                  <SelectItem value="14">{t('comparison.windowDays', { days: 14 })}</SelectItem>
                  <SelectItem value="30">{t('comparison.windowDays', { days: 30 })}</SelectItem>
                </SelectContent>
              </Select>
              {(trustworthyCount > 0 || disapprovedInImpact > 0) && (
                <span className="text-xs text-muted-foreground font-normal ml-auto flex items-center gap-3 flex-wrap">
                  {trustworthyCount > 0 && (
                  <span>
                    {t('comparison.summaryCount', '{{sig}} of {{trust}} comparable products show statistically significant changes', {
                      sig: significantCount,
                      trust: trustworthyCount,
                    })}
                  </span>
                  )}
                  {/* Die Zahl steht hier und nicht nur an den einzelnen Zeilen,
                      weil sie die AGGREGATE der Seite betrifft: Mittelwert,
                      Median und die klick-gewichtete Angabe rechnen die
                      abgelehnten Produkte mit, und deren Nachher-CTR ist aus
                      einem Grund eingebrochen, der nichts mit der Optimierung
                      zu tun hat. Herausgefiltert werden sie NICHT — das wuerde
                      die Kennzahlen aendern, ohne dass es jemand merkt. */}
                  {disapprovedInImpact > 0 && (
                    <span
                      data-testid="disapproved-summary"
                      className="flex items-center gap-1 text-warning-ink"
                    >
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                      {t('comparison.disapprovedCount', { count: disapprovedInImpact })}
                    </span>
                  )}
                  {significantCount > 0 && (
                    <>
                      <span className="text-success-ink">{t('comparison.improved', { count: improved })}</span>
                      <span className="text-destructive-ink">{t('comparison.declined', { count: declined })}</span>
                      <span>
                        {t('comparison.meanChange', 'mean')}: {formatSignedPercent(meanChange, language)}
                        {' · '}
                        {t('comparison.medianChange', 'median')}: {formatSignedPercent(medianChange, language)}
                      </span>
                    </>
                  )}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(ctrLoading || stabilityLoading) ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-md" />
                ))}
              </div>
            ) : impactProducts.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                {t('empty.noComparisonData')}
              </div>
            ) : trustworthyCount === 0 ? (
              <EmptyState
                icon={Hourglass}
                title={t('tracking.title', 'Still collecting data')}
                description={t('tracking.body', 'Optimizations have run, but no product yet has 7+ days of post-data with ≥100 impressions, ≥5 clicks, and ≥70% day-coverage on both sides. Check back in a few days.')}
              />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder={t('comparison.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pl-8 text-base md:text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    {(['all', 'improved', 'declined'] as const).map((f) => (
                      <button
                        key={f}
                        onClick={() => setImpactFilter(f)}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          impactFilter === f
                            ? f === 'improved' ? 'bg-success/10 text-success-ink'
                              : f === 'declined' ? 'bg-destructive/10 text-destructive-ink'
                              : 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {t(`comparison.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                      <SelectTrigger className="h-8 w-[200px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="change_desc">{t('comparison.sortChangeDesc', 'Best change first')}</SelectItem>
                        <SelectItem value="change_asc">{t('comparison.sortChangeAsc', 'Worst change first')}</SelectItem>
                        <SelectItem value="date_desc">{t('comparison.sortDateDesc', 'Most recent first')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Switch
                      id="only-significant"
                      checked={onlySignificant}
                      onCheckedChange={setOnlySignificant}
                    />
                    <label htmlFor="only-significant" className="text-xs text-muted-foreground cursor-pointer">
                      {t('comparison.onlySignificant', 'Only show statistically significant changes')}
                    </label>
                  </div>
                </div>
                {sortedProducts.length === 0 ? (
                  onlySignificant ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('comparison.noSignificant', 'No products have statistically significant changes yet. Try widening the window or wait for more data.')}
                      actionLabel={t('comparison.showAllTrustworthy', 'Show all trustworthy products instead')}
                      onAction={() => setOnlySignificant(false)}
                    />
                  ) : (
                    // Leergefiltert bei ausgeschaltetem Signifikanz-Filter:
                    // wahre Aussage + genau EINE Handlung, die den Zustand
                    // aufloest (Muster des Werkbank-Fixes aus Welle 3 —
                    // vorher blieb der Reset Eigenleistung).
                    <EmptyState
                      icon={SearchX}
                      title={t('comparison.noFilterResults')}
                      actionLabel={t('comparison.resetFilters')}
                      onAction={() => {
                        setSearchQuery('');
                        setImpactFilter('all');
                      }}
                    />
                  )
                ) : (
                <>
                {/* Task 12: unter 768px CSS-Breite ist eine neunspaltige Tabelle mit
                    Wilson-CI und dreistufigem Badge nur per Querscroll erreichbar -
                    genau die Ansicht, die als Screenshot-Abnahme gilt. Ab md wird die
                    Tabelle gezeigt, darunter eine Kartenform mit denselben Werten. */}
                <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px]">{t('comparison.product')}</TableHead>
                      <TableHead className="text-center">{t('comparison.liveSince', 'Live seit')}</TableHead>
                      <TableHead className="text-center">{t('comparison.beforeCtr')}</TableHead>
                      <TableHead className="text-center">{t('comparison.afterCtr')}</TableHead>
                      <TableHead className="text-center">{t('comparison.change')}</TableHead>
                      <TableHead className="text-center">{t('comparison.confidence', 'Confidence')}</TableHead>
                      <TableHead className="text-center">{t('comparison.stability', 'Stability (7/14/28d)')}</TableHead>
                      <TableHead className="text-right">{t('comparison.clicks')}</TableHead>
                      <TableHead className="text-right">{t('comparison.impressions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showAllImpact ? sortedProducts : sortedProducts.slice(0, 50)).map((row: CtrImpactRowV2) => {
                      const { change, hasMultiple, stability, disapprovedIssues } = getRowMeta(row);
                      return (
                        <TableRow key={`${row.product_id}-${row.optimization_number ?? 1}`}>
                          <TableCell className="font-medium text-sm max-w-[300px]">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="truncate" title={row.product_title}>
                                {row.product_title || '—'}
                              </span>
                              {hasMultiple && row.optimization_number && (
                                <span className="text-xs text-muted-foreground font-normal shrink-0">#{row.optimization_number}</span>
                              )}
                              {disapprovedIssues !== undefined && <DisapprovedBadge issues={disapprovedIssues} />}
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-xs text-muted-foreground">
                            {/* `optimization_number` zaehlt die Opt-Events BIS
                                ZUM Upload, `optimized_at` ist die letzte Opt
                                VOR dem Upload (RPC upload-anchored, siehe
                                usePerformance.ts) — die Beschriftung sagt
                                jetzt genau das, statt "insgesamt" zu
                                behaupten. */}
                            <span title={t('comparison.liveSinceTooltip', 'Last optimization before this upload: {{optDate}} · {{count}} opt events up to the upload', {
                              optDate: new Date(row.optimized_at).toLocaleDateString(dateLocale),
                              count: row.optimization_number,
                            })}>
                              {new Date(row.uploaded_at).toLocaleDateString(dateLocale)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {row.pre_impressions > 0 ? (
                              <div className="flex flex-col">
                                <span className="tabular-nums">{formatDecimal(Number(row.pre_ctr), language, 2)}%</span>
                                <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                                  {formatCtrCI(row.pre_ctr_ci_lower, row.pre_ctr_ci_upper, statsLocale)}
                                </span>
                              </div>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {row.post_impressions > 0 ? (
                              <div className="flex flex-col">
                                <span className="tabular-nums">{formatDecimal(Number(row.post_ctr), language, 2)}%</span>
                                <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                                  {formatCtrCI(row.post_ctr_ci_lower, row.post_ctr_ci_upper, statsLocale)}
                                </span>
                              </div>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-center">
                            {change !== null ? (
                              <span className={`inline-flex items-center gap-0.5 text-sm font-medium tabular-nums ${
                                change > 0 ? 'text-success-ink' : change < 0 ? 'text-destructive-ink' : 'text-muted-foreground'
                              }`}>
                                {change > 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : change < 0 ? <ArrowDownRight className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                                {formatSignedPercent(Number(change), language)}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <SignificanceBadge
                              zScore={row.z_score}
                              isSignificant={row.is_significant}
                              preImpressions={row.pre_impressions}
                              postImpressions={row.post_impressions}
                              preClicks={row.pre_clicks}
                              postClicks={row.post_clicks}
                              preDaysWithData={row.pre_days_with_data}
                              postDaysWithData={row.post_days_with_data}
                              effectiveWindowDays={row.effective_window_days}
                              mdeRelativePct={row.mde_relative_pct}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <StabilityChip
                              delta7={stability?.delta_7d_pct ?? null}
                              delta14={stability?.delta_14d_pct ?? null}
                              delta28={stability?.delta_28d_pct ?? null}
                            />
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                            {formatMetricNumber(row.pre_clicks, dateLocale)} → {formatMetricNumber(row.post_clicks, dateLocale)}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                            {formatMetricNumber(row.pre_impressions, dateLocale)} → {formatMetricNumber(row.post_impressions, dateLocale)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>

                <div className="md:hidden space-y-3">
                  {(showAllImpact ? sortedProducts : sortedProducts.slice(0, 50)).map((row: CtrImpactRowV2) => {
                    const { change, hasMultiple, stability, disapprovedIssues } = getRowMeta(row);
                    return (
                      <div
                        key={`card-${row.product_id}-${row.optimization_number ?? 1}`}
                        className="rounded-lg border p-3 space-y-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium break-words" title={row.product_title}>
                            {row.product_title || '—'}
                            {hasMultiple && row.optimization_number && (
                              <span className="ml-1.5 text-xs text-muted-foreground font-normal">#{row.optimization_number}</span>
                            )}
                            {disapprovedIssues !== undefined && (
                              <span className="ml-1.5 inline-block align-middle">
                                <DisapprovedBadge issues={disapprovedIssues} />
                              </span>
                            )}
                          </p>
                          {change !== null ? (
                            <span className={`inline-flex items-center gap-0.5 text-sm font-medium shrink-0 tabular-nums ${
                              change > 0 ? 'text-success-ink' : change < 0 ? 'text-destructive-ink' : 'text-muted-foreground'
                            }`}>
                              {change > 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : change < 0 ? <ArrowDownRight className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
                              {formatSignedPercent(Number(change), language)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground shrink-0">—</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('comparison.liveSince', 'Live seit')}{' '}
                          {new Date(row.uploaded_at).toLocaleDateString(dateLocale)}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('comparison.beforeCtr')}</p>
                            {row.pre_impressions > 0 ? (
                              <>
                                <p className="text-sm tabular-nums">{formatDecimal(Number(row.pre_ctr), language, 2)}%</p>
                                <p className="text-[10px] text-muted-foreground font-mono tabular-nums">
                                  {formatCtrCI(row.pre_ctr_ci_lower, row.pre_ctr_ci_upper, statsLocale)}
                                </p>
                              </>
                            ) : <p className="text-sm">—</p>}
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('comparison.afterCtr')}</p>
                            {row.post_impressions > 0 ? (
                              <>
                                <p className="text-sm tabular-nums">{formatDecimal(Number(row.post_ctr), language, 2)}%</p>
                                <p className="text-[10px] text-muted-foreground font-mono tabular-nums">
                                  {formatCtrCI(row.post_ctr_ci_lower, row.post_ctr_ci_upper, statsLocale)}
                                </p>
                              </>
                            ) : <p className="text-sm">—</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <SignificanceBadge
                            zScore={row.z_score}
                            isSignificant={row.is_significant}
                            preImpressions={row.pre_impressions}
                            postImpressions={row.post_impressions}
                            preClicks={row.pre_clicks}
                            postClicks={row.post_clicks}
                            preDaysWithData={row.pre_days_with_data}
                            postDaysWithData={row.post_days_with_data}
                            effectiveWindowDays={row.effective_window_days}
                            mdeRelativePct={row.mde_relative_pct}
                          />
                          <StabilityChip
                            delta7={stability?.delta_7d_pct ?? null}
                            delta14={stability?.delta_14d_pct ?? null}
                            delta28={stability?.delta_28d_pct ?? null}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground tabular-nums">
                          {t('comparison.clicks')}: {formatMetricNumber(row.pre_clicks, dateLocale)} → {formatMetricNumber(row.post_clicks, dateLocale)}
                          {' · '}
                          {t('comparison.impressions')}: {formatMetricNumber(row.pre_impressions, dateLocale)} → {formatMetricNumber(row.post_impressions, dateLocale)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {sortedProducts.length > 50 && (
                  <button
                    onClick={() => setShowAllImpact(!showAllImpact)}
                    className="mt-3 text-sm text-primary hover:underline"
                  >
                    {showAllImpact ? t('comparison.showLess') : t('comparison.showAll', { count: sortedProducts.length })}
                  </button>
                )}
                </>
                )}

                {/* Task 12 / N6: die Tabelle ist Screenshot-Abnahmebedingung genau
                    WEIL sie Konfidenzintervalle und ausgegraute Lock-Badges zeigt -
                    also sichtbare Unsicherheit. Genau deshalb muss der Hinweis darunter
                    stimmen, und bis zur Messung vom 26.08.2026 stand hier das GEGENTEIL
                    des Gemessenen ("Impressionen zaehlen nur den Ads-Kanal, Klicks
                    enthalten auch organische Treffer ... die CTR ist eine Untergrenze").

                    WAS WIRKLICH GEMESSEN IST, zwei getrennte Befunde. ERSTENS: die
                    Kanal-Behauptung ist an der Quelle nicht gedeckt. Googles
                    Discovery-Dokument
                    (`merchantapi.googleapis.com/$discovery/rest?version=reports_v1`,
                    Abruf 26.08.2026) kennzeichnet NUR `conversions`,
                    `conversionValue` und `conversionRate` als "Available only for the
                    FREE traffic source"; `impressions` und `clicks` tragen keine
                    Kanal-Einschraenkung. `pull-merchant-performance` zieht
                    `product_performance_view` zudem OHNE Segment `marketing_method` —
                    bezahlt und organisch sind also in BEIDEN Zahlen vermischt und nicht
                    trennbar, nicht nur in den Klicks.

                    ZWEITENS, und das ist die eigentliche Ursache der Verzerrung: Google
                    meldet Impressionen erst ab einer Mengenschwelle, Klicks dagegen
                    ohne. Gemessen ueber alle 732.849 Zeilen von `product_performance`
                    (26.08.2026) ist `min(impressions) WHERE impressions > 0` exakt 20,
                    und im GESAMTEN Bestand gibt es keine einzige Zeile mit 1 bis 19
                    Impressionen. 273.360 Zeilen (37,3 %) tragen `impressions = 0`,
                    davon 273.359 mit `clicks > 0`. Dass es eine Mengenschwelle ist und
                    kein Kanaleffekt, zeigt die Haeufigkeit: sie faellt monoton mit der
                    Klickzahl (70,2 % bei 1 Klick, 36,8 % bei 2 bis 3, 0,0 % ab 11) —
                    ein Kanaleffekt wuerde nicht mit der Klickzahl korrelieren.

                    RICHTUNG DER VERZERRUNG, und die alte Fassung hatte sie umgedreht:
                    310.863 von 667.424 Klicks (46,6 %) liegen auf Zeilen ohne gemeldete
                    Impressionen. Der Zaehler enthaelt also Klicks, fuer die der Nenner
                    nichts hat — die CTR ist systematisch ZU HOCH, eine OBERGRENZE und
                    keine Untergrenze. Das VERHAELTNIS zwischen vorher und nachher
                    bleibt brauchbar, weil beide Seiten gleich erhoben sind.

                    NICHT BELEGT und deshalb nicht in der Copy: dass die Ads-API auf
                    demselben Feed mehr Impressionen meldet als das Merchant Center
                    (28.07. bis 23.08.2026, Feed 4b906f17-31ff-4fc3-93c4-3bda2fd1a2de:
                    Merchant 45.784 Klicks / 2.407.721 Impressionen gegen Ads 44.227 /
                    4.178.385), ist gemessen; WORAN die Restdifferenz liegt, ist es
                    nicht. Die Meldeschwelle erklaert einen Teil davon, PMax-Flaechen
                    waren eine Vermutung ohne Beleg. Die Segmentierung nach
                    `marketing_method` ist ein eigener Vorgang mit Schema-Folgen und
                    hier bewusst nicht angefasst.

                    DRITTER BEFUND, SURVIVORSHIP (der zweite Satz des Hinweises).
                    `get_optimization_impact_v2` selektiert
                    `FROM products WHERE uploaded_to_gmc_at IS NOT NULL` — ein Produkt,
                    das der Sync nach dem Upload hart geloescht hat, existiert in
                    `products` nicht mehr und faellt still heraus. Gezeigt werden also
                    nur die Ueberlebenden, und die 28-Tage-Loeschrate liegt je Feed
                    zwischen 1,0 % und 41,2 % (gemessen fuer die A/B-Experimente). Die
                    RPC bleibt unangetastet: ihr Verhalten zu aendern waere eine
                    Aenderung an einer Kennzahl und keine Ehrlichkeits-Korrektur. */}
                <div
                  data-testid="ctr-data-source-notice"
                  className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2"
                >
                  <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span className="space-y-1">
                    <span className="block">
                      {t('comparison.dataSourceNotice')}{' '}
                      {t('comparison.dataSourceCaveat')}
                    </span>
                    <span className="block" data-testid="ctr-survivorship-notice">
                      {t('comparison.survivorshipNotice')}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PerformanceDashboard;
